import React from 'react';
import {SplitButton} from "primereact/splitbutton";
import {arrayToCsv, downloadBlob, isInRange, s2ab} from "../../dashboard/product-template-table/utils/utils";
import {ITimespan, IUserSignature} from "../../dashboard/product-template-table/utils/cellInterfaces";
import {IForeignKeyValue} from "../../dashboard/product-template-table/components/templates/foreign-key/foreignKey";
import {displayTimespanString} from "../../dashboard/product-template-table/components/templates/timespan/timespan";
import {IConfig, ITableDataEntry} from "../../dashboard/product-template-table/utils/interfaces";

import * as XLSX from 'xlsx-js-style';

type ExportDataTableButtonProps = {
    header: IConfig | undefined;
    body: ITableDataEntry[] | undefined
    tableName: string
}

type ProdTemplateAttrType = string | number | IUserSignature | ITimespan | IForeignKeyValue | undefined;

const ExportDataTableButton = ({header, body, tableName}: ExportDataTableButtonProps) => {

    const items = [
        {
            label: 'CSV',
            icon: 'pi pi-file',
            command: () => exportCSVFile()
        },
        {
            label: 'XLS',
            icon: 'pi pi-file-excel',
            command: () => exportExcelFile()
        }
    ]

    function getValue(value: ProdTemplateAttrType) {
        if (typeof value === "string" || typeof value === "number") {
            return value;
        } else if (!value) {
            return "N/A"
        } else if (value.hasOwnProperty("id") && value.hasOwnProperty("display") && value.hasOwnProperty("signed")) {
            const val = value as IUserSignature;
            if (val.signed) {
                return val.display
            }
            return "Unsigned"
        } else if (
            value.hasOwnProperty("days") ||
            value.hasOwnProperty("hours") ||
            value.hasOwnProperty("minutes") ||
            value.hasOwnProperty("seconds")
        ) {
            return displayTimespanString(value as ITimespan)
        } else if (
            value.hasOwnProperty("objectId") &&
            value.hasOwnProperty("represantationString") &&
            value.hasOwnProperty("template")
        ) {
            const val = value as IForeignKeyValue;
            if (val.represantationString) {
                return val.represantationString;
            }
            return "-";
        } else {
            return `Type not implemented`;
        }
    }

    const extractJSONFromProps = () => {
        if (header && body) {
            const templateArr = body.map(obj => {
                return header.data?.attributes.map(ob => {
                    const o = Object.values(obj.entries).find(entry => entry.referenceName === ob.referenceName)
                    return {name: ob.displayName.replaceAll('<br/>', ' '), value: getValue(o?.value)}
                })
            })

            if (templateArr != null) {
                return templateArr
            }
        }
        return new Array<Array<any>>
    }

    const extractArrayOfArraysFromProps = () => {
        if (header && body) {
            const headers = header.data?.attributes.map((ob) => {
                return ob.displayName.replaceAll('<br/>', ' ');
            })

            const data = body.map(obj => {
                return header.data?.attributes.map(ob => {
                    const o = Object.values(obj.entries).find(entry => entry.referenceName === ob.referenceName)
                    return getValue(o?.value)
                })
            })

            const aoa = [headers, ...data];

            if (aoa) {
                return aoa
            }
        }
        console.error("ERROR: extractArrayOfArraysFromProps")
        return new Array<Array<any>>
    }

    const handleExportExcelBinary = () => {
        if (header && body) {
            const arr = extractArrayOfArraysFromProps();
            const ws = XLSX.utils.aoa_to_sheet(arr);

            firstRowBold(ws);
            ws["!rows"] = minimalHeight(arr.length)
            ws['!cols'] = fitToColumn(arr);
            setCellBackgrounds(ws)

            const wb = {Sheets: {data: ws}, SheetNames: ['data']};
            return XLSX.write(wb, {bookType: "xlsx", bookSST: true, type: "binary", cellStyles: true});
        }
    };

    function fitToColumn(arrayOfArray: Array<Array<any>>) {
        return arrayOfArray[0].map((a, i) => ({wch: Math.max(...arrayOfArray.map(a2 => a2[i] ? a2[i].toString().length + 1 : 0))}));
    }

    function minimalHeight(rowsNumber: number) {
        let arr = []
        for (let i = 0; i < rowsNumber; i++) {
            arr.push({"hpt": 16})
        }

        return arr
    }

    function firstRowBold(ws: XLSX.WorkSheet) {
        const firstLineStyle = {font: {bold: true}};

        const range = XLSX.utils.decode_range(ws['!ref'] as string);
        range.s.r = 0; // First row
        range.e.r = 0; // First row

        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell = XLSX.utils.encode_cell({r: range.s.r, c: C});
            ws[cell].s = firstLineStyle;
        }
    }

    function setCellBackgrounds(ws: XLSX.WorkSheet) {
        if (header) {
            const styleArray = header.data.attributes.map(ob => {
                return {
                    "name": ob.displayName,
                    "defaultHighlight": ob.highlightColor,
                    "colorRange": ob.bgHighlightRanges
                }
            })

            let range = XLSX.utils.decode_range(ws['!ref'] as string);
            for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
                let columnStyle = styleArray[colNum];
                if ((columnStyle.colorRange && columnStyle.colorRange.length > 0) || columnStyle.defaultHighlight) {
                    for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) {

                        if (columnStyle && columnStyle.colorRange && columnStyle.colorRange.length > 0) {
                            let val = ws[XLSX.utils.encode_cell({r: rowNum, c: colNum})];

                            columnStyle.colorRange.forEach(ob => {
                                if (isInRange(val.v, ob)) {
                                    ws[XLSX.utils.encode_cell({r: rowNum, c: colNum})].s = {
                                        ...ws[XLSX.utils.encode_cell({
                                            r: rowNum,
                                            c: colNum
                                        })].s,
                                        fill: {
                                            fgColor: {
                                                rgb: ob.bgColor?.replaceAll("#", "")
                                            },
                                            font: {
                                                rgb: ob.color?.replaceAll("#", "")
                                            }
                                        }
                                    }
                                }
                            })
                        }

                        if (columnStyle && columnStyle.defaultHighlight
                            && !ws[XLSX.utils.encode_cell({r: rowNum, c: colNum})].s) {

                            ws[XLSX.utils.encode_cell({r: rowNum, c: colNum})].s = {
                                ...ws[XLSX.utils.encode_cell({
                                    r: rowNum,
                                    c: colNum
                                })].s,
                                fill: {
                                    fgColor: {
                                        rgb: columnStyle.defaultHighlight?.bgColor?.replaceAll("#", "")
                                    },
                                    font: {
                                        rgb: columnStyle.defaultHighlight?.color?.replaceAll("#", "")
                                    }
                                }
                            }

                        }
                    }
                }
            }
        }

    }

    const exportCSVFile = () => {
        downloadBlob(arrayToCsv(extractJSONFromProps()), `${tableName}.csv`, 'text/csv;charset=utf-8;')
    }

    const exportExcelFile = () => {
        downloadBlob(s2ab(handleExportExcelBinary()), `${tableName}.xlsx`, 'application/octet-stream')
    }

    return (
        <SplitButton label={"Export"}
                     icon='pi pi-file-export'
                     className="p-button-sm p-button-success"
                     onClick={exportExcelFile}
                     model={items}
                     disabled={!header || !body}
        />
    );
}

export {ExportDataTableButton};